import { ref } from 'vue'

import { useUserStore } from '../stores/user'

interface SessionUser {
  openId: string
  name: string
  avatarUrl: string
}

const user = ref<SessionUser | null>(null)
let inflight: Promise<SessionUser> | null = null

async function fetchMe(): Promise<SessionUser> {
  const res = await fetch('/api/auth/me', { credentials: 'same-origin' })
  if (!res.ok) throw new Error(`me failed: ${res.status}`)
  const data = (await res.json()) as SessionUser
  user.value = data

  // 同步填 Pinia userStore —— test-ground/index.vue 和 rating.vue 的
  // 草稿/评分 currentUserId 是从 userStore.user.id 读的，不填这里就会抛"用户未登录"。
  // 历史遗留：useAuth（composable）和 useUserStore（Pinia）是两套独立状态，
  // 路由守卫只调了 useAuth.ensureUser，userStore 从未被填。这里打通两者，
  // 保持只发一次 /api/auth/me 请求。
  try {
    const userStore = useUserStore()
    userStore.user = {
      id: data.openId,
      username: data.name,
      email: '',
      avatar: data.avatarUrl,
    } as typeof userStore.user
  }
  catch (e) {
    console.error('[useAuth] sync userStore failed', e)
  }

  return data
}

async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
  user.value = null
  window.location.href = '/api/auth/login'
}

/**
 * 确保 user 已加载；未登录时抛错（由路由守卫捕获跳转）。
 * 并发调用复用同一个 inflight 请求。
 */
async function ensureUser(): Promise<SessionUser> {
  if (user.value) return user.value
  if (inflight) return inflight
  inflight = fetchMe().finally(() => {
    inflight = null
  })
  return inflight
}

export function useAuth() {
  return {
    user,
    ensureUser,
    logout,
  }
}
